/**
 * @fullvsl/app-shell/views/list — VSL dashboard / inventory view (FUL-225/226/238).
 *
 * Follows the approved dashboard reference screen (fullvsl_dashboard): an
 * analytics-first operational surface — KPI summary row (Reproduções totais, Taxa de reprodução,
 * VSLs ativas) + a dense performance data table (Nome da VSL, Status, Reproduções, Play
 * Rate, Taxa de CTA, Conclusão with mini bar, Ações) + a contextual insight
 * footer (Oportunidade de otimização + Atividade recente). Per-project metrics come
 * from deterministic fixtures (project-metrics.ts) that mirror the backend
 * AnalyticsOverview shape so the live API is a drop-in swap.
 */
import type { VslProject } from "../../../contracts/src/contracts";
import type { VslStore } from "../store";
import { el, append, icon, ICONS, fmtDate } from "../dom";
import {
  getProjectMetrics,
  fmtCompact,
  fmtNumber,
  fmtPct,
  fmtTrend,
} from "../project-metrics";

export function renderListView(
  store: VslStore,
  navigate: (path: string) => void,
): HTMLElement {
  const wrapper = el("div");

  // ---- Page header ----
  const header = el("div", "fva-page-header");
  const headerText = el("div");
  append(headerText, el("h2", null, "Minhas VSLs"));
  append(headerText, el("p", null, "Gerencie e otimize suas cartas de vendas em vídeo."));

  const headerActions = el("div", "fva-page-header-actions");
  const filterBtn = el("button", "fva-btn fva-btn-secondary");
  append(filterBtn, icon(ICONS.filter), el("span", null, "Filtros"));
  const newBtn = el("button", "fva-btn fva-btn-primary");
  append(newBtn, icon(ICONS.plus), el("span", null, "Nova VSL"));
  newBtn.addEventListener("click", () => navigate("#/new"));
  append(headerActions, filterBtn, newBtn);
  append(header, headerText, headerActions);
  append(wrapper, header);

  const projects = store.list();
  if (projects.length === 0) {
    const empty = el("div", "fva-empty");
    append(empty, el("h3", null, "Nenhuma VSL ainda"));
    append(empty, el("p", null, "Crie sua primeira carta de vendas em vídeo para começar."));
    const cta = el("button", "fva-btn fva-btn-primary", "Nova VSL") as HTMLButtonElement;
    cta.addEventListener("click", () => navigate("#/new"));
    append(empty, el("div", null));
    empty.lastElementChild!.appendChild(cta);
    append(wrapper, empty);
    return wrapper;
  }

  // Derive metrics for every project once.
  const rows = projects.map((p) => ({
    project: p,
    metrics: getProjectMetrics(p.id, p.name, p.status),
  }));

  // ---- KPI summary row ----
  const published = rows.filter((r) => r.project.status === "published");
  const totalPlays = rows.reduce((s, r) => s + r.metrics.plays, 0);
  const totalImpressions = rows.reduce((s, r) => s + r.metrics.impressions, 0);
  const aggregatePlayRate = totalImpressions > 0 ? totalPlays / totalImpressions : 0;

  const kpiRow = el("div", "fva-kpi-row");
  kpiRow.appendChild(kpiCard("Reproduções totais", fmtCompact(totalPlays), "play", fmtTrend(0.052), true));
  kpiRow.appendChild(kpiCard("Taxa de reprodução", fmtPct(aggregatePlayRate), "trendingUp", fmtTrend(-0.011), false));
  kpiRow.appendChild(
    kpiCard("VSLs ativas", `${published.length}`, "rocket", `${published.length} de ${rows.length} publicadas`, null),
  );
  append(wrapper, kpiRow);

  // ---- Performance data table ----
  const tableWrap = el("div", "fva-table-wrap");
  const scroll = el("div", "fva-table-scroll");
  const table = el("table", "fva-table");

  const thead = el("thead");
  const headRow = el("tr");
  for (const h of [
    { label: "Nome da VSL", cls: "" },
    { label: "Status", cls: "" },
    { label: "Reproduções", cls: "num" },
    { label: "Taxa de reprodução", cls: "num" },
    { label: "Taxa de CTA", cls: "num" },
    { label: "Conclusão", cls: "num" },
    { label: "Ações", cls: "center" },
  ]) {
    append(headRow, el("th", h.cls || undefined, h.label));
  }
  append(thead, headRow);

  const tbody = el("tbody");
  for (const { project, metrics } of rows) {
    tbody.appendChild(renderRow(project, metrics, navigate));
  }

  append(table, thead, tbody);
  append(scroll, table);
  append(tableWrap, scroll);

  const footer = el("div", "fva-table-footer");
  append(footer, el("span", null, `Exibindo ${projects.length} VSL${projects.length === 1 ? "" : "s"} · dados de demonstração`));
  tableWrap.appendChild(footer);

  append(wrapper, tableWrap);

  // ---- Contextual insight footer ----
  append(wrapper, renderInsightFooter(rows, navigate));

  return wrapper;
}

function renderRow(
  project: VslProject,
  metrics: ReturnType<typeof getProjectMetrics>,
  navigate: (path: string) => void,
): HTMLElement {
  const draft = project.status === "draft";
  const row = el("tr");
  if (draft) row.classList.add("is-draft");
  row.style.cursor = "pointer";
  row.addEventListener("click", () => navigate(`#/vsl/${project.id}`));

  // Name + thumbnail
  const nameCell = el("td");
  const nameWrap = el("div", "fva-table-name");
  const thumb = el("div", draft ? "fva-table-thumb is-draft" : "fva-table-thumb");
  thumb.appendChild(icon(ICONS.play, "fva-nav-icon"));
  const nameText = el("div", "fva-table-name-text");
  append(nameText, el("p", null, project.name));
  append(nameText, el("span", null, `Atualizada em ${fmtDate(project.updatedAt)}`));
  append(nameWrap, thumb, nameText);
  append(nameCell, nameWrap);
  append(row, nameCell);

  // Status
  append(row, el("td", null));
  row.lastElementChild!.appendChild(
    el("span", draft ? "fva-badge fva-badge-draft" : "fva-badge fva-badge-live", draft ? "Rascunho" : "Publicada"),
  );

  // Reproduções
  append(row, el("td", "num", draft ? "—" : fmtNumber(metrics.plays)));

  // Play rate
  append(row, el("td", "num", draft ? "—" : fmtPct(metrics.playRate)));

  // CTA rate
  append(row, el("td", "num", draft ? "—" : fmtPct(metrics.ctaClickRate)));

  // Conclusão (with mini progress bar)
  const compCell = el("td", "num");
  if (draft) {
    append(compCell, el("span", "fva-text-muted", "Pendente"));
  } else {
    const prog = el("div", "fva-progress-mini");
    append(prog, el("span", "pct", fmtPct(metrics.completionRate)));
    const bar = el("div", "fva-progress-bar");
    const fill = el("span") as HTMLElement;
    fill.style.width = `${Math.round(metrics.completionRate * 100)}%`;
    append(bar, fill);
    append(prog, bar);
    append(compCell, prog);
  }
  append(row, compCell);

  // Ações
  const actionsCell = el("td", "center");
  const menuBtn = el("button", "fva-icon-btn");
  menuBtn.setAttribute("aria-label", `Ações de ${project.name}`);
  menuBtn.appendChild(icon(ICONS.moreVert, "fva-nav-icon"));
  menuBtn.addEventListener("click", (e) => {
    e.stopPropagation();
    navigate(`#/vsl/${project.id}/publish`);
  });
  actionsCell.appendChild(menuBtn);
  append(row, actionsCell);

  return row;
}

function kpiCard(
  label: string,
  value: string,
  iconName: keyof typeof ICONS,
  delta: string,
  up: boolean | null,
): HTMLElement {
  const card = el("div", "fva-kpi-card");
  const head = el("div", "fva-kpi-head");
  append(head, el("p", "fva-kpi-label", label));
  const ic = icon(ICONS[iconName], "fva-kpi-icon");
  append(head, ic);
  append(card, head);
  const valRow = el("div", "fva-kpi-valrow");
  append(valRow, el("p", "fva-kpi-value", value));
  if (up !== null) {
    const deltaEl = el("span", `fva-kpi-delta ${up ? "up" : "down"}`);
    append(deltaEl, icon(ICONS.trendingUp, "fva-kpi-delta-icon"));
    append(deltaEl, document.createTextNode(delta));
    append(valRow, deltaEl);
  } else {
    append(valRow, el("span", "fva-kpi-sub", delta));
  }
  append(card, valRow);
  return card;
}

/** Optimization opportunity + recent activity (approved insight footer). */
function renderInsightFooter(
  rows: { project: VslProject; metrics: ReturnType<typeof getProjectMetrics> }[],
  navigate: (path: string) => void,
): HTMLElement {
  const grid = el("div", "fva-insight-grid");

  // Optimization opportunity: pick the published VSL with the lowest completion
  // (most retention headroom) — mirrors the "drop-off" insight in the mockup.
  const published = rows.filter((r) => r.project.status === "published");
  const opportunity = published.length
    ? published.reduce((min, r) => (r.metrics.completionRate < min.metrics.completionRate ? r : min))
    : null;

  const oppCard = el("div", "fva-insight-opp");
  const oppBody = el("div", "fva-insight-opp-body");
  append(oppBody, el("h4", "fva-insight-title", "Oportunidade de otimização"));
  if (opportunity) {
    const dropPct = Math.max(0, Math.round((1 - opportunity.metrics.completionRate) * 100));
    append(
      oppBody,
      el(
        "p",
        "fva-insight-text",
        `A VSL "${opportunity.project.name}" tem uma boa taxa de reprodução (${fmtPct(opportunity.metrics.playRate)}), mas ${dropPct}% dos espectadores saem antes do final. Ajuste o gancho no meio do vídeo para melhorar a conclusão.`,
      ),
    );
    const analyzeBtn = el("button", "fva-insight-btn", "Analisar abandono →") as HTMLButtonElement;
    analyzeBtn.addEventListener("click", () => navigate(`#/vsl/${opportunity.project.id}/analytics`));
    append(oppBody, analyzeBtn);
  } else {
    append(oppBody, el("p", "fva-insight-text", "Publique uma VSL para visualizar oportunidades de retenção."));
  }
  const oppBg = icon(ICONS.trendingDown, "fva-insight-bg");
  append(oppCard, oppBody, oppBg);
  append(grid, oppCard);

  // Recent activity feed
  const actCard = el("div", "fva-insight-act");
  const actHead = el("div", "fva-insight-act-head");
  append(actHead, icon(ICONS.newReleases, "fva-insight-act-icon"));
  append(actHead, el("h4", "fva-insight-title", "Atividade recente"));
  const list = el("ul", "fva-insight-list");
  for (const item of recentActivity(rows)) {
    const li = el("li", "fva-insight-list-item");
    append(li, el("span", "fva-insight-list-text", item.text));
    append(li, el("span", "fva-insight-list-time", item.time));
    append(list, li);
  }
  append(actCard, actHead, list);
  append(grid, actCard);

  return grid;
}

function recentActivity(
  rows: { project: VslProject; metrics: ReturnType<typeof getProjectMetrics> }[],
): { text: string; time: string }[] {
  const items: { text: string; time: string }[] = [];
  const sorted = [...rows].sort((a, b) => (a.project.updatedAt < b.project.updatedAt ? 1 : -1));
  for (const { project } of sorted.slice(0, 3)) {
    const verb = project.status === "published" ? "desempenho atualizado" : "rascunho criado";
    items.push({ text: `VSL "${project.name}" ${verb}`, time: relTime(project.updatedAt) });
  }
  items.push({ text: "Integração com o menu personalizado do Full Funnel ativa", time: "1h atrás" });
  return items.slice(0, 3);
}

function relTime(iso: string): string {
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const hrs = Math.round(diff / 3_600_000);
    if (hrs < 1) return "agora";
    if (hrs < 24) return `${hrs}h atrás`;
    const days = Math.round(hrs / 24);
    if (days < 7) return `${days}d atrás`;
    return `${Math.round(days / 7)}w atrás`;
  } catch {
    return "recentemente";
  }
}
